/** Platform gateway API — health, analytics, deployment, services registry. */
import env from "@/config/environment";
import { API_ENDPOINTS } from "@/config/api";
import { BaseAPIService } from "@/services/api/base";
import type {
  APIResponse,
  GatewayServiceStatus,
  ServiceOperationalStatus,
  ServiceStatusesResponse,
  FeedbackSummary,
  FeedbackImprovementProposalSummary,
  FeedbackImprovementStatusPayload,
  FeedbackGuardrailRecord,
  FeedbackGuardrailFilters,
  FeedbackGuardrailActivationPayload,
  FeedbackGuardrailUpdatePayload,
  FeedbackGuardrailCreatePayload,
  LearningProfilesSummary,
  LearningRecommendationsSummary,
  LearningRecommendationParams,
} from "@/types/api";

interface APIRequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  headers?: Record<string, string>;
  data?: unknown;
  body?: string;
  timeout?: number;
  retries?: number;
}

export interface PlatformContextDataGroup {
  entity_key: string;
  source_service: string;
  entity_type: string;
  permission_key?: string;
  service_id?: string;
  sync_status?: string;
  last_sync_at?: string;
  declaration_status?: string;
  declaration_error_count?: number;
  declaration_warning_count?: number;
  declaration_issue_count?: number;
  authoring_action?: Record<string, unknown>;
  authoring_action_status?: string;
  authoring_action_type?: string;
  authoring_action_mode?: string;
  knowledge_context_source?: Record<string, unknown>;
  knowledge_context_source_mode?: string;
  knowledge_context_source_path?: string;
  knowledge_context_source_symbol?: string;
  knowledge_context_source_validation?: string;
  policy_state?: Record<string, unknown>;
  stewardship?: Record<string, unknown>;
  review_policy?: Record<string, unknown>;
  catalog_metadata?: Record<string, unknown>;
  quality_metrics?: Record<string, unknown>;
  drift_status?: Record<string, unknown>;
  search_readiness?: Record<string, unknown>;
  links?: Record<string, unknown>;
  ingestion_recipe?: Record<string, unknown>;
  retrieval_recipe?: Record<string, unknown>;
}

export interface PlatformContextCatalogPayload {
  schema_version?: string;
  catalog_revision?: number;
  source_services?: string[];
  services?: Array<Record<string, unknown>>;
  service_count?: number;
  entity_type_count?: number;
  data_group_count?: number;
  data_groups?: PlatformContextDataGroup[];
  total_count?: number;
  limit?: number;
  offset?: number;
}

export interface KnowledgePolicyGrant {
  source_service: string;
  entity_type: string;
  mode: string;
  permission_key?: string;
}

export interface KnowledgePolicyPayload {
  schema_version?: string;
  tenant_id?: string;
  package_id?: string;
  enforcement_mode?: string;
  grants?: KnowledgePolicyGrant[];
  package_grants?: KnowledgePolicyGrant[];
  searchable_sources?: string[];
  indexable_sources?: string[];
  searchable_entity_keys?: string[];
  stewardship?: Record<string, unknown>;
  review_lifecycle?: Record<string, unknown>;
  stewardship_decisions?: Array<Record<string, unknown>>;
}

export interface KnowledgePolicyBlastRadius {
  schema_version?: string;
  entity_key?: string;
  tenant_count?: number;
  evaluated_grant_mode?: string;
  proposal_applied?: boolean;
  affected_tenants?: Array<Record<string, unknown>>;
  department_impact?: Record<string, unknown>;
  document_impact?: Record<string, unknown>;
  search_impact?: Record<string, unknown>;
  tombstone_impact?: Record<string, unknown>;
}

export interface KnowledgeCatalogSimulationPayload {
  schema_version?: string;
  tenant_id?: string;
  entity_key?: string;
  policy_state?: Record<string, unknown>;
  stewardship?: Record<string, unknown>;
  search_readiness?: Record<string, unknown>;
  missing_requirements?: string[];
  blast_radius?: KnowledgePolicyBlastRadius;
}

export interface KnowledgeReviewQueuePayload {
  schema_version?: string;
  tenant_id?: string;
  items?: Array<Record<string, unknown>>;
  total_count?: number;
  limit?: number;
  offset?: number;
}

export interface PlatformSearchSourceClassification {
  schema_version: "9s.platform.search.source_classification.v1";
  source_service: string;
  entity_type: string;
  entity_key: string;
}

export interface PlatformSearchProjectionLineage {
  schema_version: "9s.platform.search.projection_lineage.v1";
  source_service: string;
  entity_type: string;
  entity_id: string;
  entity_key: string;
  document_id: string;
  source_version: string;
  deep_link: string;
}

export interface PlatformSearchHighlightSpan {
  start: number;
  end: number;
}

export interface PlatformSearchEvidenceWindow {
  text: string;
  start: number;
  end: number;
  highlight_spans?: PlatformSearchHighlightSpan[];
}

export interface PlatformSearchChunkProvenance {
  chunk_id?: string;
  chunk_index?: number;
  parent_chunk_id?: string;
  parent_excerpt?: string;
}

export interface PlatformSearchResult {
  id: string;
  entity_id: string;
  entity_type: string;
  source_service: string;
  source_classification: PlatformSearchSourceClassification;
  projection_lineage?: PlatformSearchProjectionLineage;
  title: string;
  subtitle: string;
  snippet: string;
  deep_link: string;
  score: number;
  permission_key: string;
  matched_fields: string[];
  /** Business source-event time (ISO Z); never store/index clock. */
  source_event_at?: string;
  source_kind?: "rag_primary";
  highlight_spans?: PlatformSearchHighlightSpan[];
  evidence_windows?: PlatformSearchEvidenceWindow[];
  chunk_provenance?: PlatformSearchChunkProvenance;
  freshness_bucket?: string;
  sensitivity?: string;
  owner_org_unit_id?: string;
  owner_department_id?: string;
  rank_lite?: {
    retrieval_mode?: string;
    fusion_reason?: string;
    retrieval_signal?: string;
    lexical_boost?: number;
    memory_match?: boolean;
  };
  rank_explanation?: {
    score_source?: string;
    retrieval_mode?: string;
    retrieval_signal?: string;
    source_kind?: string;
    fusion_reason?: string;
    lexical_corpus_source?: string;
    lexical_boost?: number;
    score_breakdown?: Record<string, unknown>;
    memory_match?: Record<string, unknown>;
  };
}

export interface PlatformSearchSourceStatus {
  source: string;
  label: string;
  status: "ok" | "denied" | "timeout" | "unavailable" | "error" | "invalid_response";
  latency_ms: number;
  count: number;
  source_kind?: "rag_primary";
  retrieval_summary?: Record<string, unknown>;
  query_understanding?: Record<string, unknown>;
  search_intent?: Record<string, unknown>;
  agentic_plan?: Record<string, unknown>;
  managed_retrieval_plan?: Record<string, unknown>;
  stage_results?: unknown[];
  graph_evidence?: Record<string, unknown>;
  operational_quality?: Record<string, unknown>;
  citations?: unknown[];
  error?: string;
  status_code?: number;
}

export interface PlatformSearchPayload {
  query: string;
  items: PlatformSearchResult[];
  sources: PlatformSearchSourceStatus[];
  pagination: {
    limit: number;
    total: number;
    offset?: number;
    sort?: "score" | "event_at" | string;
  };
  facets?: {
    schema_version?: "9s.platform.search.facets.v1";
    source_service_counts?: Record<string, number>;
    entity_type_counts?: Record<string, number>;
    entity_key_counts?: Record<string, number>;
  };
  audit: {
    searched_sources: string[];
    denied_sources: string[];
    error_sources: string[];
    primary_source?: string;
    fallback_sources?: string[];
    search_mode?: string;
  };
  diagnostics?: {
    search_mode?: string;
    retrieval_mode?: string;
    retrieval_policy?: string;
    retrieval_profile?: string;
    fusion_reason?: string;
    lexical_corpus_source?: string;
    lexical_chunk_count?: number;
    lexical_postings_count?: number;
    searchable_chunk_count?: number;
    lexical_coverage_ratio?: number;
    hybrid_status?: string;
    hybrid_corpus_source?: string;
    query_understanding?: Record<string, unknown>;
    search_intent?: Record<string, unknown>;
    slot_resolution?: Record<string, string>;
    unresolved_slots?: string[];
    parser_mode?: string;
    parser_status?: string;
    parser_model?: string;
    parser_error?: string;
    agentic_plan?: Record<string, unknown>;
    managed_retrieval_plan?: Record<string, unknown>;
    stage_results?: unknown[];
    stage_audit?: unknown[];
    context_governance?: Record<string, unknown>;
    retrieval_status?: string;
    graph_evidence?: Record<string, unknown>;
    natural_language?: {
      schema_version?: string;
      query_planner_status?: string;
      selected_data_groups?: string[];
      extracted_entities?: Record<string, unknown>;
      extracted_filters?: Record<string, unknown>;
      recall_modes?: string[];
      denied_data_groups?: string[];
      governance_denied_count?: number;
      vector_only?: boolean;
      low_confidence_reason?: string;
    };
    exact_recall?: {
      schema_version?: string;
      stable_identifier_terms?: string[];
      exact_candidate_count?: number;
      parser_failed_exact_recall_applied?: boolean;
      reason_code?: string;
    };
    quality?: Record<string, unknown>;
    citations?: unknown[];
    rag_primary_available?: boolean;
    candidate_count?: number;
    requested_limit?: number;
    returned_count?: number;
    source_service_counts?: Record<string, number>;
    entity_key_counts?: Record<string, number>;
    citation_count?: number;
    memory_match_count?: number;
    governance_decision_counts?: Record<string, unknown>;
    freshness_risk_count?: number;
    freshness_restricted_count?: number;
    freshness_warn_count?: number;
    freshness_max_age_days?: number | null;
    source_latencies_ms?: Record<string, unknown>;
    operational_quality?: Record<string, unknown>;
  };
}

export interface PlatformSearchParams {
  q: string;
  types?: string[];
  limit?: number;
  offset?: number;
  sort?: "score" | "event_at";
  entityTypes?: string[];
  entityKeys?: string[];
  contextIntent?: string;
  freshnessMaxAgeDays?: number;
  signal?: AbortSignal;
}

export class APIService extends BaseAPIService {
  private static instance: APIService;

  constructor() {
    super(env.API_BASE_URL || "");
  }

  public static getInstance(): APIService {
    if (!APIService.instance) {
      APIService.instance = new APIService();
    }
    return APIService.instance;
  }

  async getSystemHealth(): Promise<APIResponse<unknown>> {
    return this.get("/v1/health");
  }

  async getFrontendConfig(options?: { view?: "shell" }): Promise<APIResponse<unknown>> {
    const view = options?.view;
    if (view === "shell") {
      return this.get("/v1/config?view=shell");
    }
    return this.get("/v1/config");
  }

  async getSystemMetrics(): Promise<APIResponse<unknown>> {
    return this.get("/v1/system/metrics");
  }

  async getSystemInfo(): Promise<APIResponse<unknown>> {
    return this.get("/v1/system/info");
  }

  async getSystemStatus(): Promise<APIResponse<unknown>> {
    return this.get("/v1/system/status");
  }

  async getServices(): Promise<APIResponse<ServiceStatusesResponse>> {
    return this.get("/v1/services");
  }

  async getContextCatalog(params?: {
    limit?: number;
    offset?: number;
    q?: string;
  }): Promise<APIResponse<PlatformContextCatalogPayload>> {
    const searchParams = new URLSearchParams();
    if (typeof params?.limit === "number" && Number.isFinite(params.limit)) {
      searchParams.set("limit", String(Math.max(1, Math.floor(params.limit))));
    }
    if (typeof params?.offset === "number" && Number.isFinite(params.offset)) {
      searchParams.set("offset", String(Math.max(0, Math.floor(params.offset))));
    }
    const q = typeof params?.q === "string" ? params.q.trim() : "";
    if (q) {
      searchParams.set("q", q);
    }
    const query = searchParams.toString();
    const path = query ? `${API_ENDPOINTS.context.catalog}?${query}` : API_ENDPOINTS.context.catalog;
    return this.get(path) as Promise<APIResponse<PlatformContextCatalogPayload>>;
  }

  async simulateContextDataGroup(
    sourceService: string,
    entityType: string,
    body: { proposed_grant_mode?: string } = {},
  ): Promise<APIResponse<KnowledgeCatalogSimulationPayload>> {
    return this.post(
      API_ENDPOINTS.context.catalogDataGroupSimulate(sourceService, entityType),
      body,
    ) as Promise<APIResponse<KnowledgeCatalogSimulationPayload>>;
  }

  async replayContextService(serviceId: string): Promise<APIResponse<Record<string, unknown>>> {
    return this.post(
      API_ENDPOINTS.context.replay(serviceId),
      {},
    ) as Promise<APIResponse<Record<string, unknown>>>;
  }

  async getKnowledgePolicyEffective(): Promise<APIResponse<KnowledgePolicyPayload>> {
    return this.get(API_ENDPOINTS.knowledgePolicy.effective) as Promise<APIResponse<KnowledgePolicyPayload>>;
  }

  async getTenantKnowledgePolicy(tenantId: string): Promise<APIResponse<KnowledgePolicyPayload>> {
    return this.get(API_ENDPOINTS.knowledgePolicy.tenant(tenantId)) as Promise<APIResponse<KnowledgePolicyPayload>>;
  }

  async putTenantKnowledgePolicy(tenantId: string, body: Partial<KnowledgePolicyPayload>): Promise<APIResponse<KnowledgePolicyPayload>> {
    return this.put(
      API_ENDPOINTS.knowledgePolicy.tenant(tenantId),
      body,
    ) as Promise<APIResponse<KnowledgePolicyPayload>>;
  }

  async applyPackageKnowledgePolicy(tenantId: string): Promise<APIResponse<KnowledgePolicyPayload>> {
    return this.post(`/v1/knowledge-policy/tenants/${encodeURIComponent(tenantId)}/apply-package`) as Promise<APIResponse<KnowledgePolicyPayload>>;
  }

  async getTenantKnowledgeStewardship(tenantId: string): Promise<APIResponse<Record<string, unknown>>> {
    return this.get(`/v1/knowledge-policy/tenants/${encodeURIComponent(tenantId)}/stewardship`) as Promise<APIResponse<Record<string, unknown>>>;
  }

  async putTenantKnowledgeStewardship(tenantId: string, body: Record<string, unknown>): Promise<APIResponse<Record<string, unknown>>> {
    return this.put(`/v1/knowledge-policy/tenants/${encodeURIComponent(tenantId)}/stewardship`, body) as Promise<APIResponse<Record<string, unknown>>>;
  }

  async putTenantKnowledgeStewardshipDataGroup(
    tenantId: string,
    sourceService: string,
    entityType: string,
    body: Record<string, unknown>,
  ): Promise<APIResponse<Record<string, unknown>>> {
    return this.put(
      `/v1/knowledge-policy/tenants/${encodeURIComponent(tenantId)}/stewardship/data-groups/${encodeURIComponent(sourceService)}/${encodeURIComponent(entityType)}`,
      body,
    ) as Promise<APIResponse<Record<string, unknown>>>;
  }

  async deleteTenantKnowledgeStewardshipDataGroup(
    tenantId: string,
    sourceService: string,
    entityType: string,
  ): Promise<APIResponse<Record<string, unknown>>> {
    return this.delete(
      `/v1/knowledge-policy/tenants/${encodeURIComponent(tenantId)}/stewardship/data-groups/${encodeURIComponent(sourceService)}/${encodeURIComponent(entityType)}`,
    ) as Promise<APIResponse<Record<string, unknown>>>;
  }

  async getTenantKnowledgeOrgUnits(tenantId: string): Promise<APIResponse<Record<string, unknown>>> {
    return this.get(`/v1/knowledge-policy/tenants/${encodeURIComponent(tenantId)}/org-units`) as Promise<APIResponse<Record<string, unknown>>>;
  }

  async putTenantKnowledgeOrgUnit(
    tenantId: string,
    orgUnitId: string,
    body: Record<string, unknown>,
  ): Promise<APIResponse<Record<string, unknown>>> {
    return this.put(
      `/v1/knowledge-policy/tenants/${encodeURIComponent(tenantId)}/org-units/${encodeURIComponent(orgUnitId)}`,
      body,
    ) as Promise<APIResponse<Record<string, unknown>>>;
  }

  async deleteTenantKnowledgeOrgUnit(
    tenantId: string,
    orgUnitId: string,
  ): Promise<APIResponse<Record<string, unknown>>> {
    return this.delete(
      `/v1/knowledge-policy/tenants/${encodeURIComponent(tenantId)}/org-units/${encodeURIComponent(orgUnitId)}`,
    ) as Promise<APIResponse<Record<string, unknown>>>;
  }

  async getTenantKnowledgeReviewLifecycle(
    tenantId: string,
    options?: { view?: string },
  ): Promise<APIResponse<Record<string, unknown>>> {
    const view = typeof options?.view === "string" ? options.view.trim() : "";
    const suffix = view ? `?view=${encodeURIComponent(view)}` : "";
    return this.get(
      `/v1/knowledge-policy/tenants/${encodeURIComponent(tenantId)}/review-lifecycle${suffix}`,
    ) as Promise<APIResponse<Record<string, unknown>>>;
  }

  async getTenantKnowledgeReviewItems(
    tenantId: string,
    params?: {
      status?: string;
      entity_key?: string;
      review_mode?: string;
      q?: string;
      steward_ref?: string;
      limit?: number;
      offset?: number;
    },
  ): Promise<APIResponse<KnowledgeReviewQueuePayload>> {
    const searchParams = new URLSearchParams();
    const status = typeof params?.status === "string" ? params.status.trim() : "";
    const entityKey = typeof params?.entity_key === "string" ? params.entity_key.trim() : "";
    const reviewMode = typeof params?.review_mode === "string" ? params.review_mode.trim() : "";
    const q = typeof params?.q === "string" ? params.q.trim() : "";
    const stewardRef = typeof params?.steward_ref === "string" ? params.steward_ref.trim() : "";
    if (status) {
      searchParams.set("status", status);
    }
    if (entityKey) {
      searchParams.set("entity_key", entityKey);
    }
    if (reviewMode) {
      searchParams.set("review_mode", reviewMode);
    }
    if (q) {
      searchParams.set("q", q);
    }
    if (stewardRef) {
      searchParams.set("steward_ref", stewardRef);
    }
    if (typeof params?.limit === "number" && Number.isFinite(params.limit)) {
      searchParams.set("limit", String(Math.max(1, Math.floor(params.limit))));
    }
    if (typeof params?.offset === "number" && Number.isFinite(params.offset)) {
      searchParams.set("offset", String(Math.max(0, Math.floor(params.offset))));
    }
    const query = searchParams.toString();
    const path = query
      ? `/v1/knowledge-policy/tenants/${encodeURIComponent(tenantId)}/review-items?${query}`
      : `/v1/knowledge-policy/tenants/${encodeURIComponent(tenantId)}/review-items`;
    return this.get(path) as Promise<APIResponse<KnowledgeReviewQueuePayload>>;
  }

  async decideTenantKnowledgeReviewItem(
    tenantId: string,
    reviewId: string,
    payload: { action: string; reason?: string },
  ): Promise<APIResponse<Record<string, unknown>>> {
    const action = typeof payload.action === "string" ? payload.action.trim() : "";
    if (!action) {
      throw new Error("review decision action is required");
    }
    const body: Record<string, string> = { action };
    if (typeof payload.reason === "string" && payload.reason.trim()) {
      body.reason = payload.reason.trim();
    }
    return this.post(
      `/v1/knowledge-policy/tenants/${encodeURIComponent(tenantId)}/review-items/${encodeURIComponent(reviewId)}/decisions`,
      body,
    ) as Promise<APIResponse<Record<string, unknown>>>;
  }

  async getContextDrift(): Promise<APIResponse<Record<string, unknown>>> {
    return this.get("/v1/context/drift") as Promise<APIResponse<Record<string, unknown>>>;
  }

  async getContextProjectionOps(): Promise<APIResponse<Record<string, unknown>>> {
    return this.get("/v1/context/ops/projections") as Promise<APIResponse<Record<string, unknown>>>;
  }

  async getPreRagInbox(params?: {
    entity_key?: string;
    staging_source?: string;
    status?: string;
    review_mode?: string;
    queue?: string;
    stale?: string;
    sort?: string;
    limit?: number;
    offset?: number;
  }): Promise<APIResponse<Record<string, unknown>>> {
    const searchParams = new URLSearchParams();
    const entityKey = typeof params?.entity_key === "string" ? params.entity_key.trim() : "";
    const stagingSource = typeof params?.staging_source === "string" ? params.staging_source.trim() : "";
    const status = typeof params?.status === "string" ? params.status.trim() : "";
    const reviewMode = typeof params?.review_mode === "string" ? params.review_mode.trim() : "";
    const queue = typeof params?.queue === "string" ? params.queue.trim() : "";
    const stale = typeof params?.stale === "string" ? params.stale.trim() : "";
    const sort = typeof params?.sort === "string" ? params.sort.trim() : "";
    if (entityKey) searchParams.set("entity_key", entityKey);
    if (stagingSource) searchParams.set("staging_source", stagingSource);
    if (status) searchParams.set("status", status);
    if (reviewMode) searchParams.set("review_mode", reviewMode);
    if (queue) searchParams.set("queue", queue);
    if (stale) searchParams.set("stale", stale);
    if (sort) searchParams.set("sort", sort);
    if (typeof params?.limit === "number" && Number.isFinite(params.limit)) {
      searchParams.set("limit", String(Math.max(1, Math.floor(params.limit))));
    }
    if (typeof params?.offset === "number" && Number.isFinite(params.offset)) {
      searchParams.set("offset", String(Math.max(0, Math.floor(params.offset))));
    }
    const query = searchParams.toString();
    const path = query ? `/v1/context/pre-rag/inbox?${query}` : "/v1/context/pre-rag/inbox";
    return this.get(path) as Promise<APIResponse<Record<string, unknown>>>;
  }

  async getPreRagInboxItem(rowId: string): Promise<APIResponse<Record<string, unknown>>> {
    const normalized = typeof rowId === "string" ? rowId.trim() : "";
    return this.get(`/v1/context/pre-rag/inbox/${encodeURIComponent(normalized)}`) as Promise<
      APIResponse<Record<string, unknown>>
    >;
  }

  async decidePreRagInboxItem(
    rowId: string,
    body: { action: string; reason?: string },
  ): Promise<APIResponse<Record<string, unknown>>> {
    return this.post(`/v1/context/pre-rag/inbox/${encodeURIComponent(rowId)}/decisions`, body) as Promise<
      APIResponse<Record<string, unknown>>
    >;
  }

  async reconcileContext(): Promise<APIResponse<Record<string, unknown>>> {
    return this.post("/v1/context/reconcile", {}) as Promise<APIResponse<Record<string, unknown>>>;
  }

  async searchPlatform(params: PlatformSearchParams): Promise<APIResponse<PlatformSearchPayload>> {
    const searchParams = new URLSearchParams();
    searchParams.set("q", params.q);
    if (params.limit !== undefined) {
      searchParams.set("limit", String(params.limit));
    }
    if (params.offset !== undefined) {
      searchParams.set("offset", String(params.offset));
    }
    if (params.sort === "score" || params.sort === "event_at") {
      searchParams.set("sort", params.sort);
    }
    if (params.types && params.types.length > 0) {
      searchParams.set("types", params.types.join(","));
    }
    if (params.entityTypes && params.entityTypes.length > 0) {
      searchParams.set("entity_types", params.entityTypes.join(","));
    }
    if (params.entityKeys && params.entityKeys.length > 0) {
      searchParams.set("entity_keys", params.entityKeys.join(","));
    }
    const contextIntent = (params.contextIntent ?? "").trim();
    if (contextIntent.length > 0) {
      searchParams.set("context_intent", contextIntent);
    }
    if (typeof params.freshnessMaxAgeDays === "number" && Number.isFinite(params.freshnessMaxAgeDays)) {
      searchParams.set("freshness_max_age_days", String(Math.trunc(params.freshnessMaxAgeDays)));
    }
    return this.get(`/v1/platform/search?${searchParams.toString()}`, {
      signal: params.signal,
    }) as Promise<APIResponse<PlatformSearchPayload>>;
  }

  async getServiceHealth(serviceId: string): Promise<APIResponse<unknown>> {
    return this.get(`/v1/services/${serviceId}/health`);
  }

  async getServiceStatus(serviceId: string): Promise<APIResponse<unknown>> {
    return this.get(`/v1/services/${serviceId}/status`);
  }

  async getServiceEndpoints(serviceId: string): Promise<APIResponse<unknown>> {
    return this.get(`/v1/services/${serviceId}/endpoints`);
  }

  async registerService(serviceData: unknown): Promise<APIResponse<unknown>> {
    return this.post("/v1/services/register", serviceData);
  }

  async getCircuitBreakerStatus(serviceId: string): Promise<APIResponse<unknown>> {
    return this.get(`/v1/circuit-breakers/${serviceId}`);
  }

  async resetCircuitBreaker(serviceId: string): Promise<APIResponse<unknown>> {
    return this.post(`/v1/circuit-breakers/${serviceId}/reset`);
  }

  async getCacheStats(): Promise<APIResponse<unknown>> {
    return this.get("/v1/cache/stats");
  }

  async clearCache(): Promise<APIResponse<unknown>> {
    return this.post("/v1/cache/clear");
  }

  async getAnalyticsStats(hours: number = 24): Promise<APIResponse<unknown>> {
    const params = new URLSearchParams({ hours: String(hours) });
    const query = params.toString();
    return this.get(query.length > 0 ? `/v1/analytics/stats?${query}` : "/v1/analytics/stats");
  }

  async getLearningProfiles(intent?: string): Promise<APIResponse<LearningProfilesSummary>> {
    const params = new URLSearchParams();
    if (intent) {
      params.set("intent", intent);
    }
    const query = params.toString();
    const enrichedUrl = query.length > 0 ? `/v1/analytics/learning/profiles/enriched?${query}` : "/v1/analytics/learning/profiles/enriched";
    const basicUrl = query.length > 0 ? `/v1/analytics/learning/profiles?${query}` : "/v1/analytics/learning/profiles";
    try {
      return this.get(enrichedUrl) as Promise<APIResponse<LearningProfilesSummary>>;
    } catch {
      return this.get(basicUrl) as Promise<APIResponse<LearningProfilesSummary>>;
    }
  }

  async getLearningRecommendations(params: LearningRecommendationParams = {}): Promise<APIResponse<LearningRecommendationsSummary>> {
    const searchParams = new URLSearchParams();
    if (params.intent) {
      searchParams.set("intent", params.intent);
    }
    if (params.promoteThreshold !== undefined) {
      searchParams.set("promote_threshold", String(params.promoteThreshold));
    }
    if (params.demoteThreshold !== undefined) {
      searchParams.set("demote_threshold", String(params.demoteThreshold));
    }
    if (params.watchThreshold !== undefined) {
      searchParams.set("watch_threshold", String(params.watchThreshold));
    }
    if (params.minimumEvents !== undefined) {
      searchParams.set("minimum_events", String(params.minimumEvents));
    }
    const query = searchParams.toString();
    return this.get(query.length > 0 ? `/v1/analytics/learning/recommendations?${query}` : "/v1/analytics/learning/recommendations") as Promise<APIResponse<LearningRecommendationsSummary>>;
  }

  async getSearchTrends(): Promise<APIResponse<unknown>> {
    return this.get("/v1/analytics/search-trends");
  }

  async getDashboardStats(): Promise<APIResponse<unknown>> {
    return this.get("/v1/system/metrics");
  }

  async getDashboardOverview(): Promise<APIResponse<unknown>> {
    return this.get("/v1/dashboard/overview");
  }

  async getDashboardMetrics(): Promise<APIResponse<unknown>> {
    return this.get("/v1/monitoring/metrics");
  }

  async getMonitoringMetrics(): Promise<APIResponse<unknown>> {
    return this.get("/v1/monitoring/metrics");
  }

  async getMonitoringHealth(): Promise<APIResponse<unknown>> {
    return this.get("/v1/monitoring/health");
  }

  async getMonitoringAlerts(): Promise<APIResponse<unknown>> {
    return this.get("/v1/monitoring/alerts");
  }

  async getAnalyticsUsage(hours: number = 24): Promise<APIResponse<unknown>> {
    const params = new URLSearchParams({ hours: String(hours) });
    const query = params.toString();
    return this.get(query.length > 0 ? `/v1/analytics/usage?${query}` : "/v1/analytics/usage");
  }

  async getAnalyticsPerformance(hours: number = 24): Promise<APIResponse<unknown>> {
    const params = new URLSearchParams({ hours: String(hours) });
    const query = params.toString();
    return this.get(query.length > 0 ? `/v1/analytics/performance?${query}` : "/v1/analytics/performance");
  }

  async getAnalyticsTrends(): Promise<APIResponse<unknown>> {
    // Use analytics/search-trends as replacement for trends
    return this.get("/v1/analytics/search-trends");
  }

  async getAnalyticsUserActivity(limit: number = 50): Promise<APIResponse<unknown>> {
    const params = new URLSearchParams({ limit: String(limit) });
    const query = params.toString();
    return this.get(query.length > 0 ? `/v1/analytics/user-activity?${query}` : "/v1/analytics/user-activity");
  }

  async submitAnalyticsFeedback(feedback: string, rating: number): Promise<APIResponse<unknown>> {
    return this.post("/v1/analytics/feedback", { feedback, rating });
  }

  async getFeedbackAnalytics(hours: number = 24): Promise<APIResponse<FeedbackSummary>> {
    const params = new URLSearchParams({ hours: String(hours) });
    const query = params.toString();
    const endpoint = query.length > 0 ? `/v1/analytics/feedback?${query}` : "/v1/analytics/feedback";
    return this.get(endpoint);
  }

  async getFeedbackImprovements(status?: string, limit: number = 50): Promise<APIResponse<FeedbackImprovementProposalSummary[]>> {
    const params = new URLSearchParams();
    if (status) {
      params.set("status", status);
    }
    if (limit) {
      params.set("limit", String(limit));
    }
    const query = params.toString();
    const endpoint = query.length > 0 ? `/v1/analytics/feedback/improvements?${query}` : "/v1/analytics/feedback/improvements";
    return this.get(endpoint);
  }

  async updateFeedbackImprovementStatus(payload: FeedbackImprovementStatusPayload): Promise<APIResponse<{ status: string }>> {
    const { proposalId, status, reviewer, note } = payload;
    return this.post(`/v1/analytics/feedback/improvements/${proposalId}/status`, {
      status,
      reviewer,
      note,
    });
  }

  async applyFeedbackGuardrails(): Promise<APIResponse<{ status: string; created: number }>> {
    return this.post("/v1/analytics/feedback/guardrails/apply");
  }

  async getFeedbackGuardrails(filters: FeedbackGuardrailFilters = {}): Promise<APIResponse<FeedbackGuardrailRecord[]>> {
    const params = new URLSearchParams();
    if (filters.active !== undefined) {
      params.set("active", String(filters.active));
    }
    if (filters.severity) {
      params.set("severity", filters.severity);
    }
    if (filters.limit) {
      params.set("limit", String(filters.limit));
    }
    const query = params.toString();
    const endpoint = query.length > 0 ? `/v1/analytics/feedback/guardrails?${query}` : "/v1/analytics/feedback/guardrails";
    const response = await this.get(endpoint);
    if (!response.success || response.data === undefined) {
      return response as APIResponse<FeedbackGuardrailRecord[]>;
    }
    const data = response.data as { guardrails?: FeedbackGuardrailRecord[] } | FeedbackGuardrailRecord[];
    const guardrails = Array.isArray(data) ? data : (data.guardrails ?? []);
    return {
      ...response,
      data: guardrails,
    } as APIResponse<FeedbackGuardrailRecord[]>;
  }

  async getFeedbackGuardrail(guardrailId: string): Promise<APIResponse<FeedbackGuardrailRecord>> {
    return this.get(`/v1/analytics/feedback/guardrails/${guardrailId}`);
  }

  async setFeedbackGuardrailActivation(payload: FeedbackGuardrailActivationPayload): Promise<APIResponse<{ status: string; active: boolean }>> {
    const { guardrailId, active } = payload;
    return this.post(`/v1/analytics/feedback/guardrails/${guardrailId}/activation`, { active });
  }

  async updateFeedbackGuardrail(guardrailId: string, update: Omit<FeedbackGuardrailUpdatePayload, "guardrailId">): Promise<APIResponse<FeedbackGuardrailRecord>> {
    const body: Record<string, unknown> = {};
    if (update.keywords !== undefined) {
      body.keywords = update.keywords;
    }
    if (update.actions !== undefined) {
      body.actions = update.actions;
    }
    if (update.responsePrefix !== undefined) {
      body.response_prefix = update.responsePrefix;
    }
    if (update.responseSuffix !== undefined) {
      body.response_suffix = update.responseSuffix;
    }
    return this.patch(`/v1/analytics/feedback/guardrails/${guardrailId}`, body);
  }

  async createFeedbackGuardrail(payload: FeedbackGuardrailCreatePayload): Promise<APIResponse<FeedbackGuardrailRecord>> {
    const { proposalId, keywords, actions, responsePrefix, responseSuffix } = payload;
    const body: Record<string, unknown> = { proposal_id: proposalId };
    if (keywords !== undefined) {
      body.keywords = keywords;
    }
    if (actions !== undefined) {
      body.actions = actions;
    }
    if (responsePrefix !== undefined) {
      body.response_prefix = responsePrefix;
    }
    if (responseSuffix !== undefined) {
      body.response_suffix = responseSuffix;
    }
    return this.post("/v1/analytics/feedback/guardrails", body);
  }

  async getDatabaseHealth(): Promise<APIResponse<unknown>> {
    return this.get("/v1/system/metrics");
  }

  async getDatabaseStatus(): Promise<APIResponse<unknown>> {
    return this.get("/v1/system/status");
  }

  async getDatabaseInfo(): Promise<APIResponse<unknown>> {
    return this.get("/v1/system/info");
  }

  async getDatabaseTablesOverview(_limit = 50): Promise<APIResponse<unknown>> {
    return this.get("/v1/system/status");
  }

  async getDatabaseCollections(): Promise<APIResponse<unknown>> {
    return this.get("/v1/services");
  }

  async getDatabasePerformance(): Promise<APIResponse<unknown>> {
    return this.get("/v1/system/metrics");
  }

  async getDatabaseBackups(): Promise<APIResponse<unknown>> {
    return this.get("/v1/system/status");
  }

  async getDatabaseOperations(): Promise<APIResponse<unknown>> {
    return this.get("/v1/monitoring/metrics");
  }

  async getDatabaseManagementInfo(): Promise<APIResponse<unknown>> {
    return this.get("/v1/system/info");
  }

  async getDeploymentStatus(): Promise<APIResponse<unknown>> {
    return this.get("/v1/deployment/status");
  }

  async getDeployments(): Promise<APIResponse<unknown>> {
    return this.get("/v1/deployment/list");
  }

  async getDeploymentEnvironments(): Promise<APIResponse<unknown>> {
    return this.get("/v1/deployment/environments");
  }

  async getDeployment(deploymentId: string): Promise<APIResponse<unknown>> {
    return this.get(`/v1/deployment/${deploymentId}`);
  }

  async createDeployment(deploymentData: unknown): Promise<APIResponse<unknown>> {
    return this.post("/v1/deployment", deploymentData);
  }

  async updateDeployment(deploymentId: string, deploymentData: unknown): Promise<APIResponse<unknown>> {
    return this.put(`/v1/deployment/${deploymentId}`, deploymentData);
  }

  async deleteDeployment(deploymentId: string): Promise<APIResponse<unknown>> {
    return this.delete(`/v1/deployment/${deploymentId}`);
  }

  async restartDeployment(deploymentId: string): Promise<APIResponse<unknown>> {
    return this.post(`/v1/deployment/${deploymentId}/restart`);
  }

  async scaleDeployment(deploymentId: string, replicas: number): Promise<APIResponse<unknown>> {
    return this.post(`/v1/deployment/${deploymentId}/scale?replicas=${replicas}`);
  }

  async getDeploymentLogs(deploymentId: string, lines: number = 100): Promise<APIResponse<unknown>> {
    return this.get(`/v1/deployment/${deploymentId}/logs?lines=${lines}`);
  }

  async getDeploymentMetrics(deploymentId: string): Promise<APIResponse<unknown>> {
    return this.get(`/v1/deployment/${deploymentId}/metrics`);
  }

  async getRoutes(): Promise<APIResponse<unknown>> {
    return this.get("/v1/routes");
  }

  async getSystemResources(): Promise<APIResponse<unknown>> {
    return this.get("/v1/system/resources");
  }

  async checkServiceAvailability(serviceName: string): Promise<boolean> {
    try {
      const response = await this.getServiceHealth(serviceName);
      return response.success && (response.data as { status: string })?.status === "healthy";
    } catch {
      return false;
    }
  }

  async getServiceStatuses(): Promise<GatewayServiceStatus[]> {
    try {
      const servicesResponse = await this.get<ServiceStatusesResponse>("/v1/services/status");
      if (!servicesResponse.success || !servicesResponse.data) {
        return [];
      }

      const raw = (servicesResponse.data?.services ?? []) as unknown[];
      return raw.map((service: unknown) => {
        const s = service as Record<string, unknown>;
        const status = (s.status as ServiceOperationalStatus) ?? "unknown";
        const validStatus = ["operational", "degraded", "down", "offline"].includes(status) ? status : "unknown";
        const lastCheck = (s.lastCheck as string) ?? (s.last_check as string) ?? (s.checked_at as string) ?? new Date().toISOString();
        const responseTime = (s.responseTime as number) ?? (s.health_response_time_ms as number) ?? 0;
        return {
          id: (s.id as string) ?? "",
          name: (s.name as string) ?? "",
          status: validStatus,
          port: (s.port as number) ?? 0,
          url: (s.url as string) ?? "",
          lastCheck,
          health: (s.health as string) ?? "unknown",
          version: (s.version as string) ?? "unknown",
          responseTime,
          uptime: (s.uptime as string | number) ?? "unknown",
          endpoint_count: (s.endpoint_count as string | number) ?? "unknown",
          health_url: (s.health_url as string) ?? "",
          health_status_code: (s.health_status_code as number | null) ?? null,
          health_response_time_ms: (s.health_response_time_ms as number) ?? responseTime,
          health_error: (s.health_error as string | null) ?? null,
          decision_source: (s.decision_source as string) ?? "unknown",
          checked_at: (s.checked_at as string) ?? lastCheck,
          endpoints: (s.endpoints as Array<Record<string, unknown>>) ?? [],
        } as GatewayServiceStatus;
      });
    } catch {
      return [];
    }
  }

  async getMonitoringHealthStatuses(): Promise<GatewayServiceStatus[]> {
    const response = await this.get<unknown>("/v1/monitoring/health");
    if (!response.success) {
      const detail =
        typeof response.error === "string" && response.error.trim()
          ? response.error.trim()
          : "monitoring health request failed";
      throw new Error(detail);
    }
    const rows = response.data;
    if (!Array.isArray(rows) || rows.length < 2) {
      throw new Error("monitoring health must return Gateway aggregate rows including frontend and api-gateway");
    }
    const mapped: GatewayServiceStatus[] = rows.map((row: unknown, index: number) => {
      if (!row || typeof row !== "object") {
        throw new Error(`monitoring health items[${index}] must be an object`);
      }
      const item = row as Record<string, unknown>;
      const id = String(item.id ?? "").trim();
      const name = String(item.name ?? "").trim();
      if (!id || !name) {
        throw new Error(`monitoring health items[${index}] missing id/name`);
      }
      const rawStatus = String(item.status ?? "").trim().toLowerCase();
      if (!rawStatus) {
        throw new Error(`monitoring health ${id}: status required`);
      }
      let status: ServiceOperationalStatus;
      if (rawStatus === "healthy" || rawStatus === "up" || rawStatus === "operational" || rawStatus === "ok") {
        status = "operational";
      } else if (rawStatus === "degraded") {
        status = "degraded";
      } else if (rawStatus === "unhealthy" || rawStatus === "down" || rawStatus === "offline") {
        status = "down";
      } else {
        throw new Error(`monitoring health ${id}: unsupported status ${rawStatus}`);
      }
      const endpoint = String(item.endpoint ?? "").trim();
      const lastCheck = String(item.lastCheck ?? item.checked_at ?? "").trim();
      if (!lastCheck) {
        throw new Error(`monitoring health ${id}: lastCheck required`);
      }
      const responseTime =
        typeof item.responseTime === "number"
          ? item.responseTime
          : typeof item.health_response_time_ms === "number"
            ? item.health_response_time_ms
            : 0;
      let port = 0;
      if (endpoint) {
        try {
          const parsed = new URL(endpoint);
          port = parsed.port ? Number(parsed.port) : parsed.protocol === "https:" ? 443 : 80;
        } catch {
          throw new Error(`monitoring health ${id}: endpoint is not a valid URL`);
        }
      }
      return {
        id,
        name,
        status,
        port,
        url: endpoint,
        lastCheck,
        health: rawStatus,
        version: String(item.version ?? "unknown"),
        responseTime,
        uptime: (item.uptime as string | number | undefined) ?? "unknown",
        health_response_time_ms: responseTime,
        health_error: typeof item.error === "string" ? item.error : null,
        checked_at: lastCheck,
        endpoints: [],
      } as GatewayServiceStatus;
    });
    const byId = new Map(mapped.map((row) => [String(row.id ?? "").trim(), row]));
    if (!byId.has("api-gateway")) {
      throw new Error("monitoring health missing api-gateway row (R7b ZF-1)");
    }
    if (!byId.has("frontend")) {
      throw new Error("monitoring health missing frontend row (GATEWAY_FRONTEND_URL ZF-1)");
    }
    if (byId.get("api-gateway")?.status !== "operational") {
      throw new Error("monitoring health api-gateway must be operational");
    }
    return mapped;
  }

  async createUser(userData: unknown): Promise<APIResponse<unknown>> {
    return this.post("/v1/users", userData);
  }

  async getUsers(): Promise<APIResponse<unknown>> {
    return this.get("/v1/users");
  }

  async updateUser(userId: string, userData: unknown): Promise<APIResponse<unknown>> {
    return this.put(`/v1/users/${userId}`, userData);
  }

  async deleteUser(userId: string): Promise<APIResponse<unknown>> {
    return this.delete(`/v1/users/${userId}`);
  }

  async getDatabaseConnections(): Promise<APIResponse<unknown>> {
    return this.get("/v1/services");
  }

  async testDatabaseConnection(_connectionId: string): Promise<APIResponse<unknown>> {
    return this.get("/v1/system/status");
  }

  async createDatabaseConnection(_connection: unknown): Promise<APIResponse<unknown>> {
    throw new Error(
      "Gateway Absolute Seal: database console unmounted — create connections via owning microservice"
    );
  }

  async getDatabaseTables(_connectionId: string): Promise<APIResponse<unknown>> {
    return this.get("/v1/system/status");
  }

  async executeDatabaseQuery(_queryData: unknown): Promise<APIResponse<unknown>> {
    throw new Error(
      "Gateway Absolute Seal: database console unmounted — SQL console is not a Gateway edge surface"
    );
  }

  async getUsageAnalytics(): Promise<APIResponse<unknown>> {
    return this.getAnalyticsUsage();
  }

  async getPerformanceMetrics(): Promise<APIResponse<unknown>> {
    return this.getAnalyticsPerformance();
  }

  async getHistoricalData(): Promise<APIResponse<unknown>> {
    // Use analytics/user-activity as replacement for historical
    return this.get("/v1/analytics/user-activity");
  }

  async getDashboardData(): Promise<APIResponse<unknown>> {
    return this.getDashboardStats();
  }

  async getDashboardStatistics(): Promise<APIResponse<unknown>> {
    return this.getDashboardStats();
  }

  async getSystemUptime(): Promise<APIResponse<unknown>> {
    return this.get("/v1/system/uptime");
  }

  async getSystemPerformance(): Promise<APIResponse<unknown>> {
    // W9-B: Gateway-native monitoring has health|metrics|alerts only (no /performance).
    return this.get("/v1/monitoring/metrics");
  }

  async getServiceRegistry(): Promise<APIResponse<unknown>> {
    return this.get("/v1/services/registry");
  }

  async getRegistryServices(): Promise<APIResponse<unknown>> {
    return this.get("/v1/services/registry");
  }

  async getEcosystemRoutes(): Promise<APIResponse<unknown>> {
    return this.get("/v1/ecosystem/routes");
  }

  async makeRequest<T>(endpoint: string, options: APIRequestOptions = {}): Promise<APIResponse<T>> {
    const { method = "GET", data } = options;
    switch (method) {
      case "GET":
        return this.get(endpoint);
      case "POST":
        return this.post(endpoint, data);
      case "PUT":
        return this.put(endpoint, data);
      case "DELETE":
        return this.delete(endpoint);
      default:
        return this.get(endpoint);
    }
  }
}

export const platformApiService = APIService.getInstance();
